// src/utils/exportRealEstateOwners.js

import ExcelJS from "exceljs";
import { getRealEstateData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/realEstateOwnersData";

/**
 * Export Real Estate Owners Dashboard Data to Excel
 * Creates tables matching the exact data structure for Wilfried to compare with UI
 */
export async function exportRealEstateOwnersToExcel() {
  try {
    const data = getRealEstateData();
    const building = data.building;
    const monthlyData = data.monthlyData;
    const currentDate = new Date().toLocaleDateString("en-GB");

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== BUILDING INFO SHEET ====================
    const buildingSheet = workbook.addWorksheet("Building Info", {
      properties: { tabColor: { argb: "FF1E40AF" } }
    });

    buildingSheet.columns = [{ width: 30 }, { width: 40 }];

    // Title
    buildingSheet.mergeCells("A1:B1");
    const titleCell = buildingSheet.getCell("A1");
    titleCell.value = "BUILDING METADATA";
    titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1E40AF" }
    };
    buildingSheet.getRow(1).height = 35;

    let row = 3;
    addDataRow(buildingSheet, row++, "ID", building.id);
    addDataRow(buildingSheet, row++, "Name", building.name);
    addDataRow(buildingSheet, row++, "Location", building.location);
    addDataRow(buildingSheet, row++, "Total Area (sqm)", building.totalArea);
    addDataRow(buildingSheet, row++, "Floors Count", building.floorsCount);
    addDataRow(buildingSheet, row++, "Floor Area (sqm)", building.floorArea);
    addDataRow(buildingSheet, row++, "Capacity (people)", building.capacity);

    row++;
    addSectionHeader(buildingSheet, row++, "Operating Hours");
    addDataRow(buildingSheet, row++, "Start", building.operatingHours.start);
    addDataRow(buildingSheet, row++, "End", building.operatingHours.end);
    addDataRow(
      buildingSheet,
      row++,
      "Weekdays",
      building.operatingHours.weekdays.join(", ")
    );

    row++;
    addSectionHeader(buildingSheet, row++, "Benchmarks");
    addDataRow(
      buildingSheet,
      row++,
      "Energy Intensity (kWh/sqm)",
      building.benchmarks.energyIntensity
    );
    addDataRow(
      buildingSheet,
      row++,
      "CO2 Emissions Target (%)",
      building.benchmarks.co2Emissions
    );
    addDataRow(
      buildingSheet,
      row++,
      "ESG Score Target (%)",
      building.benchmarks.esgScore
    );
    addDataRow(
      buildingSheet,
      row++,
      "Occupancy Rate Target (%)",
      building.benchmarks.occupancyRate
    );

    row++;
    addSectionHeader(buildingSheet, row++, "Cost Factors");
    addDataRow(
      buildingSheet,
      row++,
      "Energy Cost Per kWh (€)",
      building.energyCostPerKwh
    );
    addDataRow(
      buildingSheet,
      row++,
      "CO2 Factor Per kWh (kg)",
      building.co2FactorPerKwh
    );

    row++;
    addDataRow(buildingSheet, row++, "Report Generated", currentDate);

    // ==================== ENERGY CONSUMPTION SHEET ====================
    const energySheet = workbook.addWorksheet("Energy Consumption", {
      properties: { tabColor: { argb: "FFF59E0B" } }
    });

    energySheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Intensity
      { width: 12 }, // Change %
      { width: 15 }, // Total kWh
      { width: 15 }, // Total Cost
      { width: 18 }, // Next Month Prediction
      { width: 18 }, // Top Appliances Share
      { width: 15 }, // Previous Period
      { width: 15 }, // Current Period
      { width: 12 }, // Open Office
      { width: 12 }, // Meeting Room
      { width: 10 }, // Lobby
      { width: 15 } // Conference Room
    ];

    createEnergyTable(energySheet, monthlyData);

    // ==================== HOURLY USAGE SHEET ====================
    const hourlySheet = workbook.addWorksheet("Hourly Usage", {
      properties: { tabColor: { argb: "FFFBBF24" } }
    });

    createHourlyUsageTable(hourlySheet, monthlyData);

    // ==================== CARBON EMISSIONS SHEET ====================
    const carbonSheet = workbook.addWorksheet("Carbon Emissions", {
      properties: { tabColor: { argb: "FFEF4444" } }
    });

    carbonSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 18 }, // Total Emissions
      { width: 12 }, // Change %
      { width: 12 }, // Gauge Value
      { width: 12 }, // Floor 1 %
      { width: 12 }, // Floor 1 Change
      { width: 15 }, // Floor 1 Emissions
      { width: 12 }, // Floor 2 %
      { width: 12 }, // Floor 2 Change
      { width: 15 }, // Floor 2 Emissions
      { width: 12 }, // Floor 3 %
      { width: 12 }, // Floor 3 Change
      { width: 15 }, // Floor 3 Emissions
      { width: 12 }, // Floor 4 %
      { width: 12 }, // Floor 4 Change
      { width: 15 }, // Floor 4 Emissions
      { width: 15 }, // Timeline Current
      { width: 15 } // Timeline Projected
    ];

    createCarbonTable(carbonSheet, monthlyData);

    // ==================== ESG SCORE SHEET ====================
    const esgSheet = workbook.addWorksheet("ESG Score", {
      properties: { tabColor: { argb: "FF10B981" } }
    });

    esgSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Overall Score
      { width: 12 }, // Change %
      { width: 12 }, // Gauge Value
      { width: 12 }, // Lobby %
      { width: 15 }, // Conference Room %
      { width: 15 }, // Meeting Room %
      { width: 15 }, // Open Office %
      { width: 15 }, // CO2 Reporting
      { width: 15 }, // Energy Tracking
      { width: 18 }, // GRESB Integration
      { width: 15 } // ISO14001
    ];

    createEsgTable(esgSheet, monthlyData);

    // ==================== OCCUPANCY RATE SHEET ====================
    const occupancySheet = workbook.addWorksheet("Occupancy Rate", {
      properties: { tabColor: { argb: "FF3B82F6" } }
    });

    occupancySheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 12 }, // Rate %
      { width: 12 }, // Change %
      { width: 15 }, // People Onsite
      { width: 15 }, // Total Capacity
      { width: 15 }, // Occupied %
      { width: 12 }, // Free %
      { width: 15 }, // Open Office %
      { width: 15 }, // Meeting Room %
      { width: 10 }, // Lobby %
      { width: 15 }, // Conference Room %
      { width: 18 } // Space Utilization %
    ];

    createOccupancyTable(occupancySheet, monthlyData);

    // ==================== OPERATIONAL COST SHEET ====================
    const operationalSheet = workbook.addWorksheet("Operational Cost", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });

    operationalSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 18 }, // Energy Analytics
      { width: 12 }, // Change %
      { width: 18 }, // Dynamic Occupancy
      { width: 20 }, // Utilization Analytics
      { width: 15 }, // Open Office %
      { width: 15 }, // Meeting Room %
      { width: 10 }, // Lobby %
      { width: 15 }, // Conference Room %
      { width: 15 }, // Blue Cost/sqm
      { width: 15 }, // Red Cost/sqm
      { width: 15 } // Yellow Cost/sqm
    ];

    createOperationalTable(operationalSheet, monthlyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `RealEstate-Data-${building.name.replace(
      /\s+/g,
      "-"
    )}-${currentDate.replace(/\//g, "-")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== HELPER FUNCTIONS ====================

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } }
    };
  });
  sheet.getRow(1).height = 30;
}

function styleDataCell(cell, value, isEven, format = "text") {
  cell.value = value;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: isEven ? "FFFFFFFF" : "FFF9FAFB" }
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (format === "number") {
    cell.numFmt = "#,##0";
  } else if (format === "decimal") {
    cell.numFmt = "0.0";
  } else if (format === "currency") {
    cell.numFmt = "€#,##0.00";
  } else if (format === "percent") {
    cell.numFmt = '0.0"%"';
    if (value !== null && value !== undefined) {
      cell.font = { color: { argb: value >= 0 ? "FF10B981" : "FFEF4444" } };
    }
  }
}

function createEnergyTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Intensity\n(kWh/sqm)",
    "Change\n%",
    "Total kWh",
    "Total Cost\n(€)",
    "Next Month\nPrediction (€)",
    "Top Appliances\nShare %",
    "Previous\nPeriod",
    "Current\nPeriod",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %"
  ];

  createTableHeader(sheet, headers, "FFF59E0B");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.energy.intensity,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.energy.changePercent,
      isEven,
      "percent"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.energy.totalKwh,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.energy.totalCost,
      isEven,
      "currency"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.energy.nextMonthPrediction,
      isEven,
      "currency"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      month.energy.topAppliancesShare,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      month.energy.comparison.previous,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      month.energy.comparison.current,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      month.energy.activeSpaces.openOffice,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      month.energy.activeSpaces.meetingRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      month.energy.activeSpaces.lobby,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 14),
      month.energy.activeSpaces.conferenceRoom,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createHourlyUsageTable(sheet, data) {
  sheet.columns = Array(15).fill({ width: 10 });

  const headers = [
    "Month",
    "8:00",
    "9:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "Type"
  ];

  createTableHeader(sheet, headers, "FFFBBF24");

  let rowNum = 2;
  data.forEach((month, index) => {
    // Electricity row
    const isEvenElec = rowNum % 2 === 0;
    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEvenElec);
    month.energy.hourlyUsage.electricity.forEach((val, i) => {
      styleDataCell(sheet.getCell(rowNum, i + 2), val, isEvenElec, "number");
    });
    styleDataCell(sheet.getCell(rowNum, 14), "Electricity", isEvenElec);
    sheet.getRow(rowNum).height = 20;
    rowNum++;

    // Gas row
    const isEvenGas = rowNum % 2 === 0;
    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEvenGas);
    month.energy.hourlyUsage.gas.forEach((val, i) => {
      styleDataCell(sheet.getCell(rowNum, i + 2), val, isEvenGas, "number");
    });
    styleDataCell(sheet.getCell(rowNum, 14), "Gas", isEvenGas);
    sheet.getRow(rowNum).height = 20;
    rowNum++;
  });
}

function createCarbonTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Total\nEmissions\n(kg CO₂)",
    "Change\n%",
    "Gauge\nValue %",
    "Floor 1\n%",
    "Floor 1\nChange %",
    "Floor 1\nEmissions",
    "Floor 2\n%",
    "Floor 2\nChange %",
    "Floor 2\nEmissions",
    "Floor 3\n%",
    "Floor 3\nChange %",
    "Floor 3\nEmissions",
    "Floor 4\n%",
    "Floor 4\nChange %",
    "Floor 4\nEmissions",
    "Timeline\nCurrent",
    "Timeline\nProjected"
  ];

  createTableHeader(sheet, headers, "FFEF4444");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.carbon.totalEmissions,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.carbon.changePercent,
      isEven,
      "percent"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.carbon.gaugeValue,
      isEven,
      "number"
    );

    month.carbon.floorBreakdown.forEach((floor, floorIndex) => {
      const baseCol = 6 + floorIndex * 3;
      styleDataCell(
        sheet.getCell(rowNum, baseCol),
        floor.percentage,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, baseCol + 1),
        floor.changePercent,
        isEven,
        "percent"
      );
      styleDataCell(
        sheet.getCell(rowNum, baseCol + 2),
        floor.emissions,
        isEven,
        "number"
      );
    });

    styleDataCell(
      sheet.getCell(rowNum, 18),
      month.carbon.timeline.current,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 19),
      month.carbon.timeline.projected,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createEsgTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Overall\nScore %",
    "Change\n%",
    "Gauge\nValue %",
    "Lobby\n%",
    "Conference\nRoom %",
    "Meeting\nRoom %",
    "Open\nOffice %",
    "CO2\nReporting",
    "Energy\nTracking",
    "GRESB\nIntegration",
    "ISO14001\nCompliance"
  ];

  createTableHeader(sheet, headers, "FF10B981");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.esg.overallScore,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.esg.changePercent,
      isEven,
      "percent"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.esg.gaugeValue,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.esg.spaceAllocation.lobby,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.esg.spaceAllocation.conferenceRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      month.esg.spaceAllocation.meetingRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      month.esg.spaceAllocation.openOffice,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      month.esg.compliance.co2Reporting ? "YES" : "NO",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      month.esg.compliance.energyTracking ? "YES" : "NO",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      month.esg.compliance.gresbIntegration ? "YES" : "NO",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      month.esg.compliance.iso14001 ? "YES" : "NO",
      isEven
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createOccupancyTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Rate\n%",
    "Change\n%",
    "People\nOnsite",
    "Total\nCapacity",
    "Occupied\n%",
    "Free\n%",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %",
    "Space\nUtilization %"
  ];

  createTableHeader(sheet, headers, "FF3B82F6");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.occupancy.rate,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.occupancy.changePercent,
      isEven,
      "percent"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.occupancy.peopleOnsite,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.occupancy.totalCapacity,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.occupancy.donutData.occupied,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      month.occupancy.donutData.free,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      month.occupancy.floorplanBreakdown.openOffice,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      month.occupancy.floorplanBreakdown.meetingRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      month.occupancy.floorplanBreakdown.lobby,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      month.occupancy.floorplanBreakdown.conferenceRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      month.occupancy.spaceUtilization,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createOperationalTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Energy\nAnalytics %",
    "Change\n%",
    "Dynamic\nOccupancy %",
    "Utilization\nAnalytics %",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %",
    "Blue\nCost/sqm (€)",
    "Red\nCost/sqm (€)",
    "Yellow\nCost/sqm (€)"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.operational.energyAnalytics,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.operational.changePercent,
      isEven,
      "percent"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.operational.dynamicOccupancy,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.operational.utilizationAnalytics,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.operational.spaceEfficiency.openOffice,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      month.operational.spaceEfficiency.meetingRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      month.operational.spaceEfficiency.lobby,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      month.operational.spaceEfficiency.conferenceRoom,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      month.operational.costPerSqm.blue,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      month.operational.costPerSqm.red,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      month.operational.costPerSqm.yellow,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}
